"use client";
import { sxArrayUtil } from "@util/sx-helpers";
import {
  AddRounded,
  CloseRounded,
  MenuRounded,
  RemoveRounded,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  accordionSummaryClasses,
  Divider,
  Drawer,
  IconButton,
  IconButtonProps,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { BaseHeaderProps } from "@/layout/header";
import MLink, { MLinkProps } from "@cc/m-link";
import { appbarHeight } from "@/layout/header/reactive-appbar";
import BorderedIconButton from "@cc/bordered-icon-button";

export interface NavigationDrawerProps
  extends Omit<IconButtonProps, "children"> {
  routes: BaseHeaderProps["routes"];
  onToggle?: (isDrawer: boolean) => void;
}

const NavigationDrawer = (props: NavigationDrawerProps) => {
  const { routes, onToggle, ...restIconButtonProps } = props;
  const [isDrawer, setIsDrawer] = useState(false);

  useEffect(() => {
    onToggle && onToggle(isDrawer);
  }, [isDrawer, onToggle]);
  return (
    <>
      <BorderedIconButton
        onClick={() => {
          setIsDrawer((prev) => !prev);
        }}
        color={"secondary"}
        size={"small"}
        {...restIconButtonProps}
      >
        {isDrawer ? <CloseRounded /> : <MenuRounded />}
      </BorderedIconButton>
      <Drawer
        open={isDrawer}
        variant={"temporary"}
        onClose={() => {
          setIsDrawer(false);
          onToggle && onToggle(false);
        }}
        anchor={"right"}
        PaperProps={{
          sx: (theme) => ({
            mt: appbarHeight,
            minWidth: 280,
            width: { xs: "100%", sm: 390 },
            background: theme.palette.gradient.lightToDark,
          }),
        }}
      >
        <List>
          {routes.map((route, index) => {
            const { title } = route;
            return (
              <Fragment key={`${title}-${index}`}>
                <ListItem disablePadding>
                  <NavigationDrawerItem
                    route={route}
                    setIsDrawer={setIsDrawer}
                  />
                </ListItem>
                <Divider
                  variant={"middle"}
                  sx={{
                    mx: 3,
                  }}
                />
              </Fragment>
            );
          })}
        </List>
      </Drawer>
    </>
  );
};

export default NavigationDrawer;

export interface NavigationDrawerItemProps
  extends Omit<MLinkProps, "children" | "href"> {
  route: BaseHeaderProps["routes"][number];
  setIsDrawer: (d: boolean) => void;
}

export const NavigationDrawerItem = (props: NavigationDrawerItemProps) => {
  const { route, setIsDrawer, ...restMLinkProps } = props;
  const { title, href, children } = route;
  const { sx, ...restLinkProps } = restMLinkProps;
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const handleAccordionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsAccordionOpen((prev) => !prev);
  };

  const handleContentClick = () => {
    if (href && href !== "#") {
      setIsDrawer(false);
    }
  };

  const hasValidLink = href && href !== "#";
  const hasChildren = children && children.length > 0;

  return (
    <>
      {hasChildren ? (
        <Accordion
          expanded={isAccordionOpen}
          disableGutters
          elevation={0}
          square
          sx={{
            background: "transparent",
            color: "primary.contrastText",
            width: "100%",
          }}
        >
          <AccordionSummary
            expandIcon={
              hasChildren ? (
                <IconButton
                  onClick={handleAccordionClick}
                  size="small"
                  sx={{
                    color: "primary.contrastText",
                    p: 0.5,
                  }}
                >
                  {isAccordionOpen ? <RemoveRounded /> : <AddRounded />}
                </IconButton>
              ) : null
            }
            aria-controls="panel-content"
            id="panel-header"
            sx={{
              px: 3,
              height: 48,
              padding: hasValidLink ? "0px": "default",
              color: "primary.contrastText",
              [`.${accordionSummaryClasses.expandIconWrapper}`]: {
                color: "primary.contrastText",
              },
              [`.${accordionSummaryClasses.content}`]: {
                m: 0,
                "&:hover": {
                  cursor: hasValidLink ? "pointer" : "default",
                },
              },
            }}
          >
            {hasValidLink ? (
              <MLink
                href={href}
                onClick={handleContentClick}
                sx={{
                  fontSize: "18px",
                  marginLeft: "-8px",
                  // marginRight: "32px",
                  color: "inherit",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "none",
                  },
                }}
              >
                <Typography
                  component="span"
                  textTransform="capitalize"
                  sx={{ textTransform: "capitalize" }}
                >
                  {title}
                </Typography>
              </MLink>
            ) : (
              <Typography
                fontSize={"18px"}
                component={"span"}
                textTransform={"capitalize"}
              >
                {title}
              </Typography>
            )}
          </AccordionSummary>
          <AccordionDetails
            sx={{
              py: 0,
              px: 3,
              mb: isAccordionOpen ? 1 : 0,
            }}
          >
            <List disablePadding>
              {children.map((child, index) => (
                <ListItem key={`${child.title}-${index}`} disablePadding>
                  <NavigationDrawerItem
                    route={child}
                    setIsDrawer={setIsDrawer}
                    sx={{
                      fontSize: "16px",
                      p: 2,
                      height: 40,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ) : (
        <MLink
          href={href || "#"}
          onClick={handleContentClick}
          fullWidth
          sx={[
            {
              fontSize: "18px",
              justifyContent: "flex-start",
              color: "primary.contrastText",
              textTransform: "capitalize",
              px: 3,
              height: 48,
            },
            ...sxArrayUtil(sx),
          ]}
          {...restLinkProps}
        >
          {title}
        </MLink>
      )}
    </>
  );
};
